import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import { TYPE_INTERVAL } from '../lib/consts'
import type { StateAdminRow } from '../models/state-admin'

export const STATES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Estado',
  type: 'Tipo',
  time: 'SLA',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/** Etiquetas en español de los tipos de estado (los de sistema y `process`). */
export const STATE_TYPE_LABELS: Record<string, string> = {
  process: 'Proceso',
  init: 'Inicial',
  end: 'Final',
  reject: 'Rechazo',
  rescind: 'Rescisión',
  inactive: 'Inactivación',
  undefined: 'Indefinido'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function StateActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

/** Píldora del tipo; los de sistema (≠ process) se distinguen con "Sistema". */
export function StateTypeBadge({ type }: { type: string }) {
  const label = STATE_TYPE_LABELS[type] ?? type
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      {label}
      {type !== 'process' && <span className="text-muted-foreground">· Sistema</span>}
    </span>
  )
}

/** Punto con el color configurado del estado (el que pinta tableros y chips). */
export function StateColorDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className="h-2.5 w-2.5 shrink-0 rounded-full border border-border/50"
      style={{ backgroundColor: color }}
    />
  )
}

/** SLA legible: "3 Día(s)". */
export function formatStateSla(time: number, interval: string): string {
  const label = TYPE_INTERVAL[interval as keyof typeof TYPE_INTERVAL] ?? interval
  return `${time} ${label}`
}

export const statesAdminColumns: ColumnDef<StateAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[240px]" column={column} name="Estado" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <StateColorDot color={row.original.color} />
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
      </span>
    )
  },
  {
    accessorKey: 'type',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Tipo" />,
    cell: ({ row }) => <StateTypeBadge type={row.original.type} />
  },
  {
    accessorKey: 'time',
    header: ({ column }) => <DataTableColumnHeader column={column} name="SLA" sortable={false} />,
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatStateSla(row.original.time, row.original.interval)}
      </span>
    )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <StateActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
